import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import { LocationSearch } from 'assets/templates/locationSearch/LocationSearch';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import { compact, first } from 'lodash';
import providerDeleteLogoFileApi from 'models/productManagement/provider/deleteLogoFile/Api.Provider.DeleteLogoFile';
import providerSaveApi from 'models/productManagement/provider/save/Api.Provider.Save';
import providerUploadLogoFileApi from 'models/productManagement/provider/uploadLogoFile/Api.Provider.UploadLogoFile';
import { useProviderProvider } from '../Provider.Hooks';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import useApi from 'assets/hooks/api/useApi';
import Product from 'models/productManagement/product/Model.Product';

export default function ProviderEdit() {
  const { lang } = getLocales();
  const { selected, pageRouter, listApi } = useProviderProvider();
  const productApi = useApi({ action: Product.list });

  return (
    <ModalForm<Api.Core.Provider.Save.IRequest & { logoFile?: Hooks.Input.File.Info }>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[selected]}
      title={lang.provider}
      default={{
        ...selected?.toJSON(),

        logoFile: selected?.logoMediaFile ? {
          id: selected.logoMediaFile.id,
          dataUrl: selected.logoMediaFile.url,
          name: selected.logoMediaFile.name,
          type: selected.logoMediaFile.contentType as Utils.File.MimeType,
        } : undefined
      }}
      onCancel={pageRouter.updateParams}
      onSubmit={async ({ logoFile, ...data }) => {
        const response = await providerSaveApi(data);
        if (selected?.logoMediaFile && !logoFile?.id) {
          await providerDeleteLogoFileApi(response.payload || data.id);
        }
        if (logoFile?.original) {
          await providerUploadLogoFileApi(response.payload || data.id, { formFile: logoFile.original });
        }

        if (response.status === HttpStatus.internalServerError) addLog({ error: response.message });
        listApi.execute((body) => body);
      }}
      validation={(data, errors) => {
        if (!data.name) errors.name = lang.mustNotBeEmpty;
        if (!data.streetAddress) errors.streetAddress = lang.mustNotBeEmpty;
        if (!data.city) errors.city = lang.mustNotBeEmpty;
        if (!data.provinceCode) errors.provinceCode = lang.mustNotBeEmpty;
        if (!data.countryCode) errors.countryCode = lang.mustNotBeEmpty;
        if (!data.postalCode) errors.postalCode = lang.mustNotBeEmpty;
      }}
      render={({ form }) => (
        <>
          <FormWrapper>
            <TextInput
              name="name"
              label={lang.name}
              value={form.data.name}
              error={form.errors.name}
              onChange={form.update}
              icon={<Icon class="if-quill-pen" />}
            />
            <LocationSearch
              onSelect={(location) => {
                form.update({
                  streetAddress: location?.Line1 || null,
                  provinceCode: location?.ProvinceCode || null,
                  city: location?.City || null,
                  countryCode: location?.CountryIso3 || null,
                  postalCode: location?.PostalCode || null,
                });
              }}
            />
            <InputWrapper>
              <TextInput
                name="streetAddress"
                label={lang.streetAddress}
                value={form.data.streetAddress}
                error={form.errors.streetAddress}
                onChange={form.update}
                icon={<Icon class="if-map-pin" />}
              />
              <TextInput
                name="postalCode"
                label={lang.postalCode}
                value={form.data.postalCode}
                error={form.errors.postalCode}
                onChange={form.update}
                icon={<Icon class="if-map-pin" />}
              />
              <TextInput
                name="city"
                label={lang.city}
                value={form.data.city}
                error={form.errors.city}
                onChange={form.update}
                icon={<Icon class="if-map-pin" />}
              />
              <TextInput
                name="provinceCode"
                label={lang.provinceCode}
                value={form.data.provinceCode}
                error={form.errors.provinceCode}
                onChange={form.update}
                icon={<Icon class="if-map-pin" />}
                style={{ width: Theme.Size.XXL }}
              />
              <TextInput
                name="countryCode"
                label={lang.countryCode}
                value={form.data.countryCode}
                error={form.errors.countryCode}
                onChange={form.update}
                icon={<Icon class="if-map-pin" />}
                style={{ width: Theme.Size.XXL }}
              />
            </InputWrapper>
            <SelectInput
              name="products"
              label={lang.products}
              data={productApi?.payload?.data?.map((item) => item.displayInfo)}
              isMultiselect
              value={form.data.products?.map((it) => it.id)}
              onChange={({ products: idList }) => form.update({ products: idList.map((id) => ({ id })) })}
              icon={<Icon class="fas-clipboard-list" />}
            />
            <FileInput
              name="logoFile"
              label={lang.logo}
              value={compact([form.data.logoFile])}
              fileLimit={1}
              onChange={({ logoFile }) => {
                form.update({ logoFile: first(logoFile) });
              }}
              class={ThemeFileInputDropzone}
            />
          </FormWrapper>
        </>
      )}
    />
  );
}
